import { z } from "zod/v4"

/**
 * Finance Transaction types based on API schemas
 */

export const transactionTypeSchema = z.enum(["income", "outcome"])
export type TTransactionType = z.infer<typeof transactionTypeSchema>

export const recurrenceFrequencySchema = z.enum([
  "yearly",
  "sixmonthly",
  "trimonthly",
  "bimonthly",
  "monthly",
  "biweekly",
  "weekly",
  "daily",
  "no-recurrence",
])
export type TRecurrenceFrequency = z.infer<typeof recurrenceFrequencySchema>

export const financeTransactionSchema = z.object({
  id: z.string(),
  date: z.string().or(z.date()),
  description: z.string(),
  type: transactionTypeSchema,
  value: z.number(), // In reais (decimal)
  isPaid: z.boolean(),
  isDeleted: z.boolean(),
  isReadOnly: z.boolean(),
  isChargeBack: z.boolean(),
  accountId: z.string(),
  account: z.object({ id: z.string(), name: z.string() }),
  person: z
    .object({ id: z.string(), firstName: z.string(), lastName: z.string() })
    .nullable()
    .optional(),
  createdBy: z
    .object({
      id: z.string(),
      name: z.string(),
      avatarUrl: z.string().nullable(),
    })
    .nullable()
    .optional(),
  categoryId: z.string().nullable(),
  category: z
    .object({ id: z.string(), name: z.string() })
    .nullable()
    .optional(),
  personId: z.string().nullable().optional(),
  transferId: z.string().nullable(),
  invoiceLink: z.string().nullable().optional(),
  comment: z.string().nullable().optional(),
  recurrenceId: z.string().nullable().optional(),
})
export type TFinanceTransaction = z.infer<typeof financeTransactionSchema>

export const accountsSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  paid: z.object({
    income: z.number(),
    outcome: z.number(),
  }),
  unpaid: z.object({
    income: z.number(),
    outcome: z.number(),
  }),
})
export type TAccountsSummary = z.infer<typeof accountsSummarySchema>

export type TAddTransactionRequest = {
  organizationSlug: string
  date: Date | string
  description: string
  type: TTransactionType
  value: number // In reais
  isPaid: boolean
  accountId: string
  categoryId: string
  personId?: string | null
  isReadOnly?: boolean
  comment?: string | null
  invoiceId?: string
  recurrence: TRecurrenceFrequency | null
}

export type TUpdateTransactionRequest = {
  organizationSlug: string
  description?: string
  value?: number // In reais
  isPaid?: boolean
  date?: Date | string
  comment?: string | null
  type?: TTransactionType
  accountId?: string
  categoryId?: string
  personId?: string | null
  recurrenceUpdate: "current" | "next" | "all" | null
}

export type TAddTransferRequest = {
  organizationSlug: string
  date: Date | string
  value: number // In reais
  isPaid: boolean
  fromAccountId: string
  toAccountId: string
}

export type TListTransactionsResponse = {
  transactions: TFinanceTransaction[]
}

export type TListPendingTransactionsResponse = {
  transactions: TFinanceTransaction[]
}

export type TGetAccountsSummaryResponse = {
  summary: TAccountsSummary[]
}
